import { io, Socket } from 'socket.io-client';
import { WebSocketProvider, WebSocketConfig, WebSocketMessage } from './types';

export class SocketIOProvider extends WebSocketProvider {
  private socket: Socket | null = null;
  private manualDisconnect = false;

  constructor(config: WebSocketConfig) {
    super(config);
  }

  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
  }

  connect(): void {
    if (this.socket) {
      console.warn('Socket.io already connected');
      return;
    }

    this.socket = io(this.getWebSocketUrl(), {
      path: '/ws',
      transports: ['websocket'],
      reconnection: !this.manualDisconnect,
    });

    this.socket.on('connect', () => {
      this.connected = true;
      this.manualDisconnect = false;
      console.log('Socket.io Connected');
      
      // Authorize after connection
      this.socket?.emit('authorize', {
        groupIdentifier: this.config.deviceId,
        sessionIdentifier: this.config.sessionId,
        isAdmin: false,
      });

      if (this.config.onConnect) {
        this.config.onConnect();
      }
    });

    this.socket.on('disconnect', (reason) => {
      this.connected = false;
      console.log('Socket.io Disconnected', reason);
      
      if (this.config.onDisconnect) {
        this.config.onDisconnect(reason);
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket.io Error:', error);
      
      if (this.config.onError) {
        this.config.onError(error);
      }
    });

    this.socket.on('pageUpdate', (message: WebSocketMessage) => {
      if (message.requireAck && message.messageId) {
        this.sendAck(message.messageId);
      }
      
      this.config.onPageUpdate(message);
    });
  }

  disconnect(): void {
    this.manualDisconnect = true;
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  isConnected(): boolean {
    return this.connected && this.socket !== null;
  }

  sendAck(messageId: string): void {
    if (this.socket) {
      this.socket.emit('ack', { messageId });
    }
  }

  setManualDisconnect(value: boolean): void {
    this.manualDisconnect = value;
    // Update socket.io reconnection setting if socket exists
    if (this.socket) {
      this.socket.io.opts.reconnection = !value;
    }
  }
}
